import { Container, Graphics, Ticker, Point, BlurFilter, Color } from "pixi.js";

interface ParticleData {
    graphic: Graphics;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
}

/**
 * Enhanced game scene with physics-based particle field
 * and 'neon bloom' aesthetics for PixiJS v8.
 */
export class Game {

    private _view!: Container;
    private _particleContainer!: Container;
    private _sprites: Container[] = [];
    private _colors: number[] = [0x00ffff, 0xff00ff, 0xffff00, 0x00ff00, 0xff4d4d];
    private _ticker!: Ticker;
    private _particles: ParticleData[] = [];
    private _mousePos: Point = new Point(0, 0);
    private _isMouseDown: boolean = false;

    constructor() {
        this._sprites = [];
        this.construction();
        this.init();
    }

    private construction(): void {
        this._view = new Container();
        this._view.label = "GameContainer";

        this._particleContainer = new Container();
        this._particleContainer.label = "ParticleContainer";
        // Apply a subtle bloom-like effect using blur and additive blending
        const blur = new BlurFilter({ strength: 2 });
        this._particleContainer.filters = [blur];
        // this._particleContainer.blendMode = "add"; // Standard ADD blending for 'glow'
    }

    private init(): void {
        this.createBackground();
        this._view.addChild(this._particleContainer);
        this.createSprites();
        this.setupInteraction();

        // Animation ticker
        this._ticker = new Ticker();
        this._ticker.add(this.onTick, this);
        this._ticker.start();
    }

    private createBackground(): void {
        const bg: Graphics = new Graphics();
        bg.label = "Background";

        // Fill background with a deep space gradient-like color
        bg.rect(0, 0, window.innerWidth, window.innerHeight);
        bg.fill(0x0a0a1a);

        // Add subtle reactive grid
        this.drawGrid(bg);

        this._view.addChild(bg);
    }

    private drawGrid(bg: Graphics): void {
        bg.stroke({ width: 1, color: 0x1a1a3a });
        for (let x: number = 0; x <= window.innerWidth; x += 60) {
            bg.moveTo(x, 0);
            bg.lineTo(x, window.innerHeight);
        }
        for (let y: number = 0; y <= window.innerHeight; y += 60) {
            bg.moveTo(0, y);
            bg.lineTo(window.innerWidth, y);
        }
    }

    private createSprites(): void {
        const centerX: number = window.innerWidth / 2;
        const centerY: number = window.innerHeight / 2;

        for (let i: number = 0; i < 5; i++) {
            const sprite: Container = this.createColoredBox(
                this._colors[i],
                `Box${i + 1}`
            );

            sprite.x = centerX + (i * 120 - 240);
            sprite.y = centerY;

            this._sprites.push(sprite);
            this._view.addChild(sprite);
        }
    }

    private createColoredBox(color: number, name: string): Container {
        const container: Container = new Container();
        container.label = name;

        const graphics: Graphics = new Graphics();
        graphics.label = `${name}Graphics`;

        // Draw box with a slight 'glow' via multiple strokes
        graphics.rect(-40, -40, 80, 80);
        graphics.fill({ color, alpha: 0.8 });
        graphics.stroke({ width: 2, color: 0xffffff, alpha: 0.5 });
        graphics.stroke({ width: 6, color, alpha: 0.2 });

        container.addChild(graphics);

        // Make interactive
        container.eventMode = "static";
        container.cursor = "pointer";

        container.on("pointerover", () => {
            graphics.scale.set(1.1);
        });
        container.on("pointerout", () => {
            graphics.scale.set(1.0);
        });

        return container;
    }

    private setupInteraction(): void {
        const interactionLayer: Graphics = new Graphics();
        interactionLayer.label = "InteractionLayer";
        interactionLayer.rect(0, 0, window.innerWidth, window.innerHeight);
        interactionLayer.fill({ color: 0x000000, alpha: 0 });
        interactionLayer.eventMode = "static";
        interactionLayer.cursor = "crosshair";

        interactionLayer.on("pointermove", (event) => {
            this._mousePos.copyFrom(event.global);
        });

        interactionLayer.on("pointerdown", () => {
            this._isMouseDown = true;
        });

        interactionLayer.on("pointerup", () => {
            this._isMouseDown = false;
        });

        interactionLayer.on("pointerupoutside", () => {
            this._isMouseDown = false;
        });

        this._view.addChildAt(interactionLayer, 1);
    }

    private spawnParticle(x: number, y: number, isExplosion: boolean = false): void {
        const color: number = this._colors[Math.floor(Math.random() * this._colors.length)];
        const graphic = new Graphics();

        const size = isExplosion ? 2 + Math.random() * 4 : 1 + Math.random() * 2;
        graphic.circle(0, 0, size);
        graphic.fill(color);
        // graphic.blendMode = "add";

        graphic.x = x;
        graphic.y = y;

        let vx, vy;
        if (isExplosion) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 2 + Math.random() * 5;
            vx = Math.cos(angle) * speed;
            vy = Math.sin(angle) * speed;
        } else {
            vx = (Math.random() - 0.5) * 2;
            vy = (Math.random() - 0.5) * 2;
        }

        const particle: ParticleData = {
            graphic,
            vx,
            vy,
            life: 1.0,
            maxLife: 0.01 + Math.random() * 0.02
        };

        this._particles.push(particle);
        this._particleContainer.addChild(graphic);
    }

    private onTick(): void {
        const time: number = Date.now() * 0.001;
        const centerY: number = window.innerHeight / 2;

        // Animate boxes
        for (let i: number = 0; i < this._sprites.length; i++) {
            const sprite: Container = this._sprites[i];
            sprite.y = centerY + Math.sin(time * 0.8 + i) * 60;
            sprite.rotation = Math.sin(time * 0.4 + i) * 0.15;

            // Interaction with mouse
            const dx = this._mousePos.x - sprite.x;
            const dy = this._mousePos.y - sprite.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 200) {
                const force = (200 - dist) / 200;
                sprite.x -= dx * force * 0.02;
                sprite.y -= dy * force * 0.02;
            }
        }

        // Spawn particles at mouse position
        if (this._isMouseDown || Math.random() > 0.5) {
            const count = this._isMouseDown ? 5 : 1;
            for (let i = 0; i < count; i++) {
                this.spawnParticle(
                    this._mousePos.x + (Math.random() - 0.5) * 20,
                    this._mousePos.y + (Math.random() - 0.5) * 20,
                    this._isMouseDown
                );
            }
        }

        // Update particles
        for (let i = this._particles.length - 1; i >= 0; i--) {
            const p = this._particles[i];

            // Physics: Attract to mouse
            const dx = this._mousePos.x - p.graphic.x;
            const dy = this._mousePos.y - p.graphic.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < 300) {
                const force = (300 - dist) / 3000;
                p.vx += dx * force;
                p.vy += dy * force;
            }

            // Friction
            p.vx *= 0.98;
            p.vy *= 0.98;

            p.graphic.x += p.vx;
            p.graphic.y += p.vy;

            // Life
            p.life -= p.maxLife;
            p.graphic.alpha = p.life;
            p.graphic.scale.set(p.life);

            if (p.life <= 0) {
                this._particleContainer.removeChild(p.graphic);
                p.graphic.destroy();
                this._particles.splice(i, 1);
            }
        }

        // Constrain particle count for performance
        if (this._particles.length > 1000) {
            const removed = this._particles.splice(0, this._particles.length - 1000);
            removed.forEach(p => {
                this._particleContainer.removeChild(p.graphic);
                p.graphic.destroy();
            });
        }
    }

    public onResize(): void {
        const bg = this._view.children.find(c => c.label === "Background") as Graphics;
        if (bg) {
            bg.clear();
            bg.rect(0, 0, window.innerWidth, window.innerHeight);
            bg.fill(0x0a0a1a);
            this.drawGrid(bg);
        }

        const interactionLayer = this._view.children.find(c => c.label === "InteractionLayer") as Graphics;
        if (interactionLayer) {
            interactionLayer.clear();
            interactionLayer.rect(0, 0, window.innerWidth, window.innerHeight);
            interactionLayer.fill({ color: 0, alpha: 0 });
        }

        const centerX: number = window.innerWidth / 2;
        for (let i: number = 0; i < this._sprites.length; i++) {
            const sprite: Container = this._sprites[i];
            sprite.x = centerX + (i * 120 - 240);
        }
    }

    get view(): Container {
        return this._view;
    }

}

